import { useMemo, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ShowDataRow, ShowDataRowAdapter } from "./MgrShowDataRow";
import apiClient from "../../../api/apiClient";
import { ShowData } from "../Show";
import { ExpenseData } from "../Expense";
import { ColDef, IDetailCellRendererParams } from "@ag-grid-community/core";
import { shortDateString } from "../../../utils/dateFormatters";
import { useTeamService } from "../../../api/teamService";

const MgrShowAnalytics = () => {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const [rowData, setRowData] = useState<ShowDataRow[]>();
  const { teamsForMember } = useTeamService();
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      aggFunc: "count",
      minWidth: 200,
      suppressAutoSize: false,
      filter: true,
      resizable: true,
      enableRowGroup: true,
    };
  }, []);
  const detailCellRendererParams = useMemo<any>(() => {
    return {
      detailGridOptions: {
        columnDefs: [
          { field: "name", headerName: "Name" },
          {
            field: "tag",
            headerName: "Tag",
            filter: true,
            enableRowGroup: true,
            sort: "desc",
          },
          {
            field: "cost",
            headerName: "Projected Cost",
            valueFormatter: (p: any) =>
              p.value ? "$" + Math.floor(p.value).toLocaleString() : "0",
            minWidth: 150,
          },
          {
            field: "actualCost",
            headerName: "Actual Cost",
            valueFormatter: (p: any) =>
              p.value ? "$" + Math.floor(p.value).toLocaleString() : "0",
            minWidth: 150,
          },
        ],
        groupDefaultExpanded: 1,
        rowGroupPanelShow: "always",
      },
      getDetailRowData: (params: any) => {
        params.successCallback(params.data.children);
      },
    } as IDetailCellRendererParams;
  }, []);
  const [columnDefs, _] = useState<ColDef[]>([
    {
      field: "performanceDate",
      headerName: "Date",
      cellRenderer: "agGroupCellRenderer",
      sort: "desc",
      minWidth: 150,
      type: "dateString",
      valueFormatter: (p: any) => {
        return shortDateString(p.value);
      },
    },
    { field: "team", headerName: "Artist" },
    { field: "venue", headerName: "Venue" },
    { field: "city", headerName: "City", minWidth: 125 },
    { field: "state", headerName: "State", minWidth: 125 },
    { field: "dealType", headerName: "Deal Type", minWidth: 150 },
    { field: "status", headerName: "Status", minWidth: 125 },

    {
      field: "guarantee",
      aggFunc: "sum",
      headerName: "Guarantee",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      filter: false,
    },
    {
      field: "productionBuyouts",
      headerName: "Production Buyouts",
      aggFunc: "sum",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      filter: false,
    },
    {
      field: "walkoutPotential",
      aggFunc: "sum",
      headerName: "Walkout Potential",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 175,
      filter: false,
    },
    {
      field: "walkout",
      headerName: "Walkout",
      aggFunc: "sum",
      minWidth: 150,
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      filter: false,
    },
    {
      field: "sellableCap",
      aggFunc: "sum",
      headerName: "Sellable Cap",
      valueFormatter: (p: any) => Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      filter: false,
    },
    {
      field: "ticketsSold",
      aggFunc: "sum",
      headerName: "Tickets Sold",
      minWidth: 150,
      filter: false,
    },
    { field: "appearingWith", headerName: "Appearing With" },
    { field: "artistNet", headerName: "Artist Net" },
    {
      field: "expensesBudget",
      headerName: "Expenses (Forecast)",
      aggFunc: "sum",
      minWidth: 200,
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      filter: false,
    },
    {
      field: "expensesActual",
      headerName: "Expenses (Actual)",
      aggFunc: "sum",
      minWidth: 200,
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      filter: false,
    },
  ]);

  const getData = async () => {
    try {
      const shows: ShowData[] = await apiClient
        .get("/multi-team/show", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((resp) => resp.data);
      const expenses: ExpenseData[] = await apiClient
        .get("/expense", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((resp) => resp.data);

      const finalOutput = new ShowDataRowAdapter().toContracts(
        shows,
        expenses,
        teamsForMember
      );
      setRowData(finalOutput);
    } catch (error) {
      setRowData([]);
      throw error;
    }
  };

  useEffect(() => {
    getData();
  }, []);
  return (
    <div style={containerStyle}>
      <div style={gridStyle} className={"ag-theme-quartz"}>
        <AgGridReact<ShowDataRow>
          rowData={rowData}
          masterDetail={true}
          columnDefs={columnDefs}
          sideBar={{ toolPanels: ["columns", "filters"] }}
          rowGroupPanelShow={"always"}
          enableCharts={true}
          cellSelection={true}
          defaultColDef={defaultColDef}
          detailCellRendererParams={detailCellRendererParams}
        />
      </div>
    </div>
  );
};

export default MgrShowAnalytics;
